import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Gift, Zap, Star, CheckCircle, Check, AlertTriangle } from 'lucide-react';
import { useSubscription } from '@/context/SubscriptionContext';
import { PLANS } from '@/config/subscriptionPlans';
import { launchPurchaseFlow, BILLING_PRODUCT_IDS, BILLING_BASE_PLAN_IDS, BILLING_UNAVAILABLE_MESSAGE, getProProductId } from '@/services/billingService';
import { formatTrialExpiresAt } from '@/services/trialService';
import { Capacitor } from '@capacitor/core';

export function PlanUpgradeModal({ isOpen, onClose }) {
  const {
    plan, setPlan, isFree, isPremiumPersonal, isPremiumPro,
    cachedProducts, restorePurchases, billingAvailable,
    trialPlan, trialExpiresAt, trialDaysRemaining,
    purchasedMaxProfiles,
    registerPendingPurchase,
  } = useSubscription();

  const [purchasing, setPurchasing] = useState(false);
  const [personalCycle, setPersonalCycle] = useState('yearly');
  const [proCycle, setProCycle] = useState('yearly');
  const [proProfiles, setProProfiles] = useState(2);
  useEffect(() => {
  if (
    isOpen &&
    Number.isInteger(purchasedMaxProfiles)
  ) {
    setProProfiles(purchasedMaxProfiles);
  }
}, [isOpen, purchasedMaxProfiles]);

  // Billing no disponible en Android nativo cuando billingAvailable === false
  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  const showBillingUnavailable = isAndroidNative && billingAvailable === false;

  // Trial activo solo si el plan efectivo PRO viene del trial,
  // no de una suscripción/entitlement server-side.
  const isOnTrial =
    trialPlan === PLANS.PREMIUM_PRO &&
    !Number.isInteger(purchasedMaxProfiles);

  // Premium Pro real según el plan efectivo server-authoritative.
  const isPremiumProReal =
  isPremiumPro &&
  !isOnTrial;
  const hasPurchasedPro = Number.isInteger(purchasedMaxProfiles);

const isCurrentProSelection =
  hasPurchasedPro &&
  proProfiles === purchasedMaxProfiles;

const proReplacementMode =
  hasPurchasedPro && proProfiles !== purchasedMaxProfiles
    ? proProfiles > purchasedMaxProfiles
      ? 'upgrade'
      : 'downgrade'
    : undefined;

const canChangePurchasedPro =
  isPremiumProReal &&
  hasPurchasedPro &&
  !isCurrentProSelection;
  const proMonthlyPrice = 9.95 + (proProfiles - 1) * 7.95;
  const proYearlyPrice = proMonthlyPrice * 12 * 0.8;
  const proYearlyMonthlyEquivalent = proYearlyPrice / 12;

  const formatPrice = (value) =>
    value.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
     });

  // ── Helper: obtiene el offerToken correcto del caché de productos ──
  const getOfferToken = (productId, basePlanId) => {
    const match = cachedProducts.find(
      p =>
        p.productId === productId &&
        p.basePlanId === basePlanId &&
        p.offerId === ''
    );

    return match?.offerToken ?? null;
  };

  // ── Lanzar compra real o fallback en web/dev ──
  const handleSelectPlan = async (
  productId,
  basePlanId,
  replacementMode
) => {
    if (!isAndroidNative) {
      setPlan(productId);
      onClose();
      return;
    }

    const offerToken = getOfferToken(productId, basePlanId);

    if (!offerToken) {
      console.error('[BILLING] No offerToken found for', productId, basePlanId);
      return;
    }

    setPurchasing(true);

    try {
      // Registrar el productId que se acaba de intentar comprar,
      // para verificación puntual si el listener purchasesUpdated no procesa correctamente.
      registerPendingPurchase(productId);

      await launchPurchaseFlow(
  productId,
  offerToken,
  replacementMode
);
      // El plan se actualizará automáticamente vía listener en SubscriptionContext
      onClose();
    } catch (err) {
      console.error('[BILLING] Purchase error:', err);
      // Si launchPurchaseFlow falla antes de abrir correctamente Billing,
      // limpiar el pendiente para no dejar estado huérfano.
      registerPendingPurchase(null);
    } finally {
      setPurchasing(false);
    }
  };

  // ── Textos de botones ──
  const personalButtonLabel = isPremiumPersonal
  ? 'Tu plan actual'
  : isPremiumProReal
    ? 'Premium Pro activo'
    : purchasing
      ? 'Procesando…'
      : 'Activar Premium Personal';

  const proButtonLabel =
  purchasing
    ? 'Procesando…'
    : isPremiumProReal
      ? canChangePurchasedPro
        ? `Cambiar a ${proProfiles} perfiles`
        : 'Tu plan actual'
      : 'Activar Premium Pro';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#121212] text-white border-[#2A2A2A]">
        <DialogHeader>
          <DialogTitle className="text-xl text-white">
            Elige tu plan
          </DialogTitle>

          <p className="text-sm text-[#909090] mt-1">
            Disfruta Rutapp Pro gratis durante 60 días. Sin tarjeta y sin compromiso.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">

          {/* ── TARJETA DE ESTADO ACTUAL ── */}

          {/* Estado: Trial Pro activo */}
          {isOnTrial && (
            <div className="flex items-start gap-3 rounded-xl border border-blue-500/40 bg-blue-500/10 p-4">
              <Gift className="h-5 w-5 mt-0.5 shrink-0 text-blue-400" />

              <div className="flex flex-col gap-1">
                <span className="font-semibold text-blue-200 text-sm">
                  Estás usando Rutapp Pro gratis
                </span>

                <span className="text-blue-300 text-sm">
                  {trialDaysRemaining > 1
                    ? `Te quedan ${trialDaysRemaining} días de prueba.`
                    : trialDaysRemaining === 1
                      ? 'Te queda 1 día de prueba.'
                      : 'Tu prueba expira hoy.'}
                </span>

                {trialExpiresAt && (
                  <span className="text-blue-400 text-xs">
                    Finaliza el {formatTrialExpiresAt(trialExpiresAt)}
                  </span>
                )}

                <span className="text-[#7090B0] text-xs mt-0.5">
                  Sin tarjeta. Sin cobro automático.
                </span>

                <span className="text-[#5878A0] text-xs">
                  Al terminar podrás seguir en Free o activar Premium.
                </span>
              </div>
            </div>
          )}

          {/* Estado: Premium Pro real */}
          {isPremiumProReal && (
            <div className="flex items-center gap-3 rounded-xl border border-blue-500/40 bg-blue-500/10 p-4">
              <Zap className="h-5 w-5 shrink-0 text-blue-400" />
              <span className="font-semibold text-blue-200 text-sm">
                Premium Pro activo
              </span>
            </div>
          )}

          {/* Estado: Premium Personal */}
          {isPremiumPersonal && (
            <div className="flex items-center gap-3 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-4">
              <Star className="h-5 w-5 shrink-0 text-yellow-400" />
              <span className="font-semibold text-yellow-200 text-sm">
                Premium Personal activo
              </span>
            </div>
          )}

          {/* Estado: Free */}
          {isFree && (
            <div className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] p-4">
              <CheckCircle className="h-5 w-5 shrink-0 text-[#505050]" />
              <span className="font-semibold text-[#808080] text-sm">
                Estás en modo Free
              </span>
            </div>
          )}

          {/* ── Aviso de Billing no disponible ── */}
          {showBillingUnavailable && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
              <span>{BILLING_UNAVAILABLE_MESSAGE}</span>
            </div>
          )}

          {/* ── TARJETA FREE ── */}
          <div className={`rounded-xl border p-4 space-y-3 ${
            isFree && !isOnTrial
              ? 'border-[#404040] bg-[#1E1E1E]'
              : 'border-[#1E1E1E] bg-[#141414]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#505050]" />
                  <h3 className="font-semibold text-base text-white">
                    Free
                  </h3>
                </div>

                <p className="text-lg font-bold text-white mt-1">
                  Gratis
                </p>
              </div>

              {isFree && !isOnTrial && (
                <span className="text-xs bg-[#2A2A2A] text-[#909090] rounded-full px-2.5 py-1 font-medium">
                  Plan actual
                </span>
              )}
            </div>

            <ul className="space-y-2 text-sm text-[#808080]">
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[#505050] shrink-0" />
                1 perfil
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[#505050] shrink-0" />
                Hasta 3 clientes por día
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[#505050] shrink-0" />
                Google Maps
              </li>

              <li className="flex items-center gap-2 opacity-35">
                <span className="h-3.5 w-3.5 shrink-0 text-center leading-none text-xs">
                  ✕
                </span>
                <span className="line-through">
                  Funciones avanzadas
                </span>
              </li>
            </ul>

            <Button
              variant="outline"
              className="w-full border-[#2A2A2A] text-[#707070] bg-transparent cursor-default"
              disabled
            >
              {isFree && !isOnTrial ? 'Plan actual' : 'Plan Free'}
            </Button>
          </div>

          {/* ── TARJETA PREMIUM PERSONAL ── */}
          <div className={`rounded-xl border p-4 space-y-3 ${
            isPremiumPersonal
              ? 'border-yellow-500/50 bg-yellow-500/5'
              : 'border-[#2A2A2A] bg-[#161616]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <h3 className="font-semibold text-base text-white">
                    Premium Personal
                  </h3>
                </div>

                <div className="flex gap-1 mt-1 mb-1">
                  <button
                    type="button"
                    onClick={() => setPersonalCycle('monthly')}
                    className={`text-xs px-2 py-1 rounded ${
                      personalCycle === 'monthly'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-[#242424] text-[#909090]'
                    }`}
                  >
                    Mensual
                  </button>

                  <button
                    type="button"
                    onClick={() => setPersonalCycle('yearly')}
                    className={`text-xs px-2 py-1 rounded ${
                      personalCycle === 'yearly'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-[#242424] text-[#909090]'
                    }`}
                  >
                    Anual · -20%
                  </button>
                </div>

                {personalCycle === 'monthly' ? (
                  <p className="text-lg font-bold text-white mt-1">
                    9,95 €/mes
                  </p>
                ) : (
                  <div className="mt-1">
                    <p className="text-lg font-bold text-white">
                      7,96 €/mes
                    </p>
                    <p className="text-xs text-[#909090]">
                      Facturado 95,52 €/año
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end gap-1.5">
                {isPremiumPersonal && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-300 rounded-full px-2.5 py-1 font-medium">
                    Tu plan actual
                  </span>
                )}

                <span className="text-xs bg-[#1E1E1E] text-[#707070] rounded-full border border-[#2A2A2A] px-2.5 py-1">
                  Plan individual
                </span>
              </div>
            </div>

            <ul className="space-y-2 text-sm text-[#B0B0B0]">
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                1 perfil
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                Clientes ilimitados
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                Nota informativa, web e imagen rápida
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                WhatsApp rápido con clientes
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                Estados de ruta
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                Nombre de empresa editable
              </li>

              <li className="flex items-center gap-2 opacity-40">
                <span className="h-3.5 w-3.5 shrink-0 text-center leading-none text-xs">
                  ✕
                </span>
                <span className="line-through">
                  Funciones entre equipos
                </span>
              </li>
            </ul>

            <Button
              className="w-full bg-yellow-600 hover:bg-yellow-500 text-white"
              disabled={
  isPremiumPersonal ||
  isPremiumProReal ||
  purchasing ||
  showBillingUnavailable
}
              onClick={() =>
                handleSelectPlan(
                  BILLING_PRODUCT_IDS.PREMIUM_PERSONAL,
                  BILLING_BASE_PLAN_IDS.PREMIUM_PERSONAL[personalCycle]
                )
              }
            >
              {personalButtonLabel}
            </Button>
          </div>

          {/* ── TARJETA PREMIUM PRO (destacada) ── */}
          <div className={`rounded-xl border-2 p-4 space-y-3 ${
            isPremiumPro
              ? 'border-blue-500 bg-blue-500/5'
              : 'border-blue-500/40 bg-[#13151F]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-400" />
                  <h3 className="font-semibold text-base text-white">
                    Premium Pro
                  </h3>
                </div>

                <div className="flex gap-1 mt-1 mb-1">
                  <button
                    type="button"
                    onClick={() => setProCycle('monthly')}
                    className={`text-xs px-2 py-1 rounded ${
                      proCycle === 'monthly'
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#242424] text-[#909090]'
                    }`}
                  >
                    Mensual
                  </button>

                  <button
                    type="button"
                    onClick={() => setProCycle('yearly')}
                    className={`text-xs px-2 py-1 rounded ${
                      proCycle === 'yearly'
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#242424] text-[#909090]'
                    }`}
                  >
                    Anual · -20%
                  </button>
                </div>
<div className="mt-2">
  <label className="block text-xs text-[#909090] mb-1">
    Número de perfiles
  </label>

  <select
    value={proProfiles}
    onChange={(e) => setProProfiles(Number(e.target.value))}
    className="w-full rounded-md bg-[#242424] border border-[#3A3A3A] text-white text-sm px-3 py-2"
  >
    {Array.from({ length: 9 }, (_, index) => index + 2).map((count) => (
      <option key={count} value={count}>
        {count} perfiles
      </option>
    ))}
  </select>
</div>
                {proCycle === 'monthly' ? (
  <p className="text-lg font-bold text-white mt-1">
    {formatPrice(proMonthlyPrice)} €/mes
  </p>
) : (
  <div className="mt-1">
    <p className="text-lg font-bold text-white">
      {formatPrice(proYearlyMonthlyEquivalent)} €/mes
    </p>
    <p className="text-xs text-[#909090]">
      Facturado {formatPrice(proYearlyPrice)} €/año
    </p>
  </div>
)}
              </div>

              <div className="flex flex-col items-end gap-1.5">
                {isOnTrial && (
                  <span className="text-xs bg-blue-500/20 text-blue-300 rounded-full px-2.5 py-1 font-medium">
                    Tu prueba actual
                  </span>
                )}

                {isPremiumProReal && (
  <span className="text-xs bg-blue-500/20 text-blue-300 rounded-full px-2.5 py-1 font-medium">
    {Number.isInteger(purchasedMaxProfiles)
      ? `Tu plan actual · ${purchasedMaxProfiles} perfiles`
      : 'Tu plan actual'}
  </span>
)}

                <span className="text-xs bg-blue-600/25 text-blue-300 rounded-full border border-blue-500/30 px-2.5 py-1 font-medium">
                  Recomendado
                </span>
              </div>
            </div>

            <ul className="space-y-2 text-sm text-[#C0C8D8]">
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                Varios perfiles
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                Clientes ilimitados
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                Todo lo de Premium Personal
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                Alertas entre perfiles
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                WhatsApp entre equipos
              </li>

              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                Estados compartidos entre perfiles
              </li>
            </ul>

            <Button
  className={`w-full text-white font-medium ${
    isPremiumProReal && !canChangePurchasedPro
      ? 'bg-blue-700/50 cursor-default'
      : 'bg-blue-600 hover:bg-blue-500'
  }`}
  disabled={
    showBillingUnavailable ||
    purchasing ||
    (isPremiumProReal && !canChangePurchasedPro)
  }
  onClick={() =>
    handleSelectPlan(
      getProProductId(proProfiles),
      BILLING_BASE_PLAN_IDS.PREMIUM_PRO[proProfiles][proCycle],
      proReplacementMode
    )
  }
>
  {proButtonLabel}
</Button>
          </div>

          {/* ── Restaurar / Gestionar ── */}
          <div className="pt-2 space-y-1 border-t border-[#1E1E1E]">
            <Button
              variant="ghost"
              className="w-full text-[#606060] hover:text-white hover:bg-[#1E1E1E] text-sm"
              onClick={restorePurchases}
              disabled={purchasing}
            >
              Restaurar compra
            </Button>

            <Button
              variant="ghost"
              className="w-full text-[#606060] hover:text-white hover:bg-[#1E1E1E] text-sm"
              onClick={() => {
                window.open(
                  'https://play.google.com/store/account/subscriptions?package=com.rutapp.app',
                  '_system'
                );
              }}
            >
              Gestionar suscripción
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}